import { useQuery, useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Building2, ChevronDown, Check, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLocation } from "wouter";

interface AccountSwitcherProps {
  collapsed?: boolean;
}

export default function AccountSwitcher({ collapsed = false }: AccountSwitcherProps) {
  const [, setLocation] = useLocation();
  const rawAllConfigs = useQuery(api.meta.allConfigs, {});
  const allConfigs = {
    data: rawAllConfigs ? rawAllConfigs.map((c) => ({ ...c, id: c._id })) : undefined,
    isLoading: rawAllConfigs === undefined,
  };
  const rawActiveConfig = useQuery(api.meta.getConfig, {});
  const activeConfig = {
    data: rawActiveConfig ? { ...rawActiveConfig, id: rawActiveConfig._id } : undefined,
  };

  const switchAccountMutation = useMutation(api.meta.switchAccount);
  const [isSwitching, setIsSwitching] = useState(false);
  const switchMutation = {
    isPending: isSwitching,
    mutate: async (args: { configId: any }) => {
      setIsSwitching(true);
      try {
        await switchAccountMutation(args);
        toast.success("Conta de anúncio alterada com sucesso!");
      } catch (err: any) {
        toast.error("Erro ao trocar conta: " + (err?.message ?? String(err)));
      } finally {
        setIsSwitching(false);
      }
    },
  };

  const accounts = (allConfigs.data || []).map((a: any) => ({ ...a, id: a.id ?? a._id }));
  const active = accounts.find((a) => a.isActive);

  // No accounts configured
  if (!activeConfig.data && !allConfigs.isLoading) {
    return (
      <button
        onClick={() => setLocation("/settings")}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 hover:bg-amber-500/15 transition-colors text-amber-400 text-xs"
      >
        <Plus className="h-3.5 w-3.5" />
        {!collapsed && <span>Conectar conta</span>}
      </button>
    );
  }

  // Loading
  if (allConfigs.isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-muted-foreground text-xs">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {!collapsed && <span>Carregando...</span>}
      </div>
    );
  }

  // Always show dropdown (even with 1 account) so user can add more
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-card/50 border border-border/50 hover:bg-card/80 hover:border-border transition-all text-xs max-w-[220px] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <div className="h-6 w-6 rounded-md bg-primary/15 flex items-center justify-center shrink-0">
            <Building2 className="h-3.5 w-3.5 text-primary" />
          </div>
          {!collapsed && (
            <>
              <div className="min-w-0 flex-1 text-left">
                <p className="text-foreground font-medium truncate leading-tight">
                  {active?.adAccountName || "Conta Meta"}
                </p>
                <p className="text-muted-foreground text-[10px] truncate leading-tight">
                  {active?.adAccountId}
                </p>
              </div>
              <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
          Contas de Anúncio ({accounts.length})
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {accounts.map((account) => (
          <DropdownMenuItem
            key={account.id}
            onClick={() => {
              if (!account.isActive) {
                switchMutation.mutate({ configId: account.id });
              }
            }}
            className={`cursor-pointer flex items-center gap-3 py-2 ${
              account.isActive ? "bg-primary/5" : ""
            }`}
            disabled={switchMutation.isPending}
          >
            <div className="h-7 w-7 rounded-md bg-card flex items-center justify-center border shrink-0">
              <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">
                {account.adAccountName || "Conta Meta"}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {account.adAccountId}
              </p>
            </div>
            {account.isActive && (
              <Check className="h-4 w-4 text-primary shrink-0" />
            )}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => setLocation("/settings")}
          className="cursor-pointer text-muted-foreground"
        >
          <Plus className="h-4 w-4 mr-2" />
          Adicionar conta
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
